import "./Select.scss";
import { ChevronArrowRight, Close } from "../../../assets/icons";
import React, { useState, useEffect, useRef } from "react";
import NoDataIcon from "../../../assets/icons/NoDataFound";
import { debounce } from "../../../utilities/commonUtilities";
import { useAppSelector } from "../../../store/hooks/StoreHooks";

interface SelectProps {
  label?: string;
  options: { [key: string]: any }[];
  onChange: (
    selectedOption: { [key: string]: any },
    name?: string | undefined
  ) => void;
  selectedValue?: string | number;
  fontSize?: string;
  width?: string;
  className?: string;
  padding?: string;
  optionFontSize?: string;
  placeholder?: string;
  disabled?: boolean;
  name?: string;
  showCloseIcon?: boolean;
  onClear?: () => void;
  isNotFound?: any;
  isSearch?: boolean;
  onChangeSearch?: any;
  styles?: { [key: string]: any };
  required?: boolean;
  isWrap?: boolean;
  onCloseDropdown?: any;
  targetName?: string;
  textInput?: boolean;
}

const Select: React.FC<SelectProps> = ({
  label,
  options,
  onChange,
  selectedValue = "",
  fontSize = "14px",
  width,
  className,
  padding,
  optionFontSize = "14px",
  placeholder = "Select an option",
  disabled = false,
  name,
  showCloseIcon = false,
  onClear,
  isNotFound,
  isSearch = false,
  onChangeSearch,
  styles,
  required,
  isWrap = false,
  onCloseDropdown,
  targetName,
  textInput
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [optionsList, setOptionsList] = useState(options);
  
  const { customerEnabled, customerCreated } = useAppSelector((state) => state.auth);
  const { projectReadOnly } = useAppSelector((state) => state.projectHeader);

  const [selectedOption, setSelectedOption] = useState<{
    [key: string]: any;
  } | null>();

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const baseList = Array.isArray(options) ? options : [];

    let foundSelected: any = null;
    const hasSelectedValue = selectedValue !== undefined && selectedValue !== null && selectedValue !== "";

    if (hasSelectedValue) {
      foundSelected = baseList.find((opt: any) =>
        opt?.value == selectedValue || opt?.id == selectedValue || opt?.label == selectedValue || opt?.name == selectedValue
    ) || null;
  }

    if (!foundSelected && hasSelectedValue) {
      const syntheticOption = {
        label: typeof selectedValue === "string" ? selectedValue : String(selectedValue),
        value: selectedValue,
        id: selectedValue,
      } as any;
      setOptionsList([syntheticOption, ...baseList]);
      setSelectedOption(syntheticOption);
    } else {
      setOptionsList(baseList);
      setSelectedOption(foundSelected);
    }
  }, [options, selectedValue]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        if (name === targetName) {
          onCloseDropdown && onCloseDropdown();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, name]);


  const handleSelectOption = (
    option: { [key: string]: any },
    name?: string
  ) => {
    if (!disabled && ((!customerEnabled && !projectReadOnly )|| (customerEnabled && customerCreated))) {
      setSelectedOption(option);
      setIsOpen(false);
      onChange(option, name);
    }
  };

  const handleClearSelection = () => {
    if (!disabled && ((!customerEnabled && !projectReadOnly )|| (customerEnabled && customerCreated))) {
      setSelectedOption(null);
      onClear && onClear();
    }
  };

  return (
    <>
      {
        <div
          className={`relative ${className}`}
          ref={dropdownRef}
          style={{ width }}
          data-name={name}
        >
          {label && (
            <label className="block text-[14px] text-neutral-25 mb-1">
              {label}
              {required && <span className="text-error-400 ml-1">*</span>}
            </label>
          )}
          <div onClick={() => (!disabled && ((!customerEnabled && !projectReadOnly ) || (customerEnabled && customerCreated))) && setIsOpen(!isOpen)}
            className={`relative w-full border rounded-[8px] bg-transparent ${padding} ${(disabled || (customerEnabled && !customerCreated) || projectReadOnly) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              } ${isNotFound ? "border-error-700" : "border-darkcolor-400"}`}
          >
            <div className="flex items-center">
              {
                textInput ?
                  <input
                    className={`text-neutral-25 bg-transparent focus:outline-none w-full rounded-[8px]`}
                    style={{ fontSize }}
                    type="text"
                    placeholder={placeholder}
                    disabled={(customerEnabled && !customerCreated) || projectReadOnly}
                    value={selectedValue}
                    onChange={(e) => { onChange(e, name) }}
                  /> :
                  <span
                    className={`block ${isWrap ? "break-all pr-[2.25rem] " : 'truncate'} text-neutral-25`}
                    style={{ fontSize }}
                  >
                    {selectedOption?.value === "-2" ? placeholder : selectedOption?.label || placeholder}
                  </span>
              }

              <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                {showCloseIcon && selectedValue && (
                  <Close
                    className="cursor-pointer"
                    onClick={handleClearSelection}
                    disabled={(customerEnabled && !customerCreated) || projectReadOnly}
                    size={18}
                  />
                )}
                {showCloseIcon && selectedValue && ((customerEnabled && !customerCreated) || projectReadOnly) && (
                  <ChevronArrowRight
                    className={`transform transition-transform cursor-pointer ${isOpen ? "-rotate-90" : "rotate-90"
                      }`}
                    size={20}
                  />
                )}
              </span>
            </div>
          </div>

          {isOpen && (
            <div
              className="border-[1px] border-darkcolor-400 origin-top-right absolute right-0 mt-[3px] w-full rounded-[8px] bg-darkcolor-700 max-h-[200px] overflow-y-auto"
              style={{ zIndex: 99999 }}
            >
              {isSearch && (
                <div className="p-[10px] bg-darkcolor-800">
                  <input
                    onChange={debounce(onChangeSearch, 300)}
                    type="text"
                    name={"search"}
                    disabled={(customerEnabled && !customerCreated) || projectReadOnly}
                    className="flex px-[12px] py-[10px] rounded-[8px] border-[1px] border-darkcolor-400 w-full bg-transparent text-neutral-25 focus:outline-none text-[16px]"
                  />
                </div>
              )}
              {optionsList.length > 0 ? (
                optionsList.map((option, index) => {
                  return (
                    <div
                      key={option.id ?? (option.value !== undefined ? `${option.value}-${index}` : `${option.label ?? "opt"}-${index}`)}
                      className={`text-[16px] p-[10px] hover:bg-darkcolor-600  cursor-pointer  flex justify-between bg-darkcolor-800 ${index === 0 && "rounded-t-[8px]"
                        } ${index === optionsList.length - 1 ? "rounded-b-[8px]" : ""} `}
                      onClick={() => handleSelectOption(option, name)} 
                      style={{ zIndex: 999999 }}
                    >
                      <p
                        className={`ml-[8px] text-neutral-25 font-Mulish ${selectedOption?.label === option?.label ? "text-primary-400" : ""}`}
                        style={{ fontSize: optionFontSize }}
                      >
                        {option?.label || option?.name}
                      </p>
                    </div>
                  )
                }
                )
              ) : (
                <div className="flex flex-col justify-center items-center p-[10px]">
                  <NoDataIcon size={60} />
                  <p className="text-neutral-25 text-[14px] mt-[5px]">
                    No Data Found
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      }
    </>

  );
};

export default Select;
